import { MaintenanceCategory } from '../types/index.js';

export type MaintenanceCategoryDetail = {
  summary: string;
  placeholder: string;
};

/** Display order for maintenance category pickers (powertrain → driveline → rolling → chassis → misc). */
export const MAINTENANCE_CATEGORY_OPTIONS: MaintenanceCategory[] = [
  MaintenanceCategory.ENGINE,
  MaintenanceCategory.OIL,
  MaintenanceCategory.SPARK,
  MaintenanceCategory.FUEL,
  MaintenanceCategory.COOLING,
  MaintenanceCategory.CARBURETOR,
  MaintenanceCategory.CLUTCH,
  MaintenanceCategory.CHAIN,
  MaintenanceCategory.GEARS,
  MaintenanceCategory.TIRES,
  MaintenanceCategory.BRAKES,
  MaintenanceCategory.STEERING,
  MaintenanceCategory.BEARINGS,
  MaintenanceCategory.CHASSIS,
  MaintenanceCategory.BODYWORK,
  MaintenanceCategory.ELECTRICAL,
  MaintenanceCategory.GENERAL,
];

export const MAINTENANCE_CATEGORY_DETAILS: Record<MaintenanceCategory, MaintenanceCategoryDetail> = {
  [MaintenanceCategory.ENGINE]: {
    summary: 'Top-end, bottom-end, seals, gaskets, or full engine rebuilds.',
    placeholder: 'Top-end refresh, new piston/rings, leak-down check...',
  },
  [MaintenanceCategory.OIL]: {
    summary: 'Engine oil, gearbox oil, chain lube, and lubrication service.',
    placeholder: 'Engine oil change, gearbox oil, chain wax/lube...',
  },
  [MaintenanceCategory.SPARK]: {
    summary: 'Spark plug, coil, stator, timing, and ignition checks.',
    placeholder: 'New NGK plug gapped to spec, checked timing, replaced coil...',
  },
  [MaintenanceCategory.FUEL]: {
    summary: 'Fuel lines, filter, pump, tank, and delivery system checks.',
    placeholder: 'Replaced fuel line, cleaned filter, checked pump flow...',
  },
  [MaintenanceCategory.COOLING]: {
    summary: 'Radiator, hoses, water pump, and coolant system service.',
    placeholder: 'Flushed radiator, new hoses, bled cooling system...',
  },
  [MaintenanceCategory.CARBURETOR]: {
    summary: 'Jetting, cleaning, rebuild kits, idle sync, and carb tuning.',
    placeholder: 'Cleaned carb, changed main jet to 142, synced idle...',
  },
  [MaintenanceCategory.CLUTCH]: {
    summary: 'Clutch shoes, drum, springs, and engagement tuning.',
    placeholder: 'Replaced clutch shoes, cleaned drum, adjusted engagement...',
  },
  [MaintenanceCategory.CHAIN]: {
    summary: 'Chain, master links, tension, and wear inspection.',
    placeholder: 'New chain, adjusted tension, replaced master link...',
  },
  [MaintenanceCategory.GEARS]: {
    summary: 'Driver, axle sprockets, gear ratio changes, and alignment.',
    placeholder: 'Changed from 18/61 to 16/58, aligned sprockets...',
  },
  [MaintenanceCategory.TIRES]: {
    summary: 'Mounting, pressures, wear inspection, and compound changes.',
    placeholder: 'Mounted new MG reds, set pressures to 10.5 / 11 psi...',
  },
  [MaintenanceCategory.BRAKES]: {
    summary: 'Pads, rotors, hydraulics, bleed, and pedal travel.',
    placeholder: 'Bled lines, replaced pads, adjusted pedal free play...',
  },
  [MaintenanceCategory.STEERING]: {
    summary: 'Steering wheel, column, spindles, and front-end geometry checks.',
    placeholder: 'Checked spindle play, torqued steering hardware...',
  },
  [MaintenanceCategory.BEARINGS]: {
    summary: 'Wheel, hub, axle, and steering bearings.',
    placeholder: 'Replaced rear hub bearings, repacked front wheel bearings...',
  },
  [MaintenanceCategory.CHASSIS]: {
    summary: 'Frame checks, caster/camber, ride height, and hardware torque.',
    placeholder: 'Checked caster/camber, torqued frame bolts, adjusted ride height...',
  },
  [MaintenanceCategory.BODYWORK]: {
    summary: 'Body panels, seat, nerf bars, bumpers, and cosmetic repairs.',
    placeholder: 'Replaced seat struts, repaired side pod, adjusted nerf bars...',
  },
  [MaintenanceCategory.ELECTRICAL]: {
    summary: 'Wiring, battery, sensors, MyChron, and data system checks.',
    placeholder: 'Secured loom, replaced battery, verified MyChron pickup...',
  },
  [MaintenanceCategory.GENERAL]: {
    summary: 'Catch-all for inspections, event prep, or multi-system work.',
    placeholder: 'Pre-event nut-and-bolt, general inspection, cleaned bodywork...',
  },
};

export function maintenanceCategoryLabel(category: MaintenanceCategory) {
  return category.charAt(0) + category.slice(1).toLowerCase();
}
